import * as Headless from '@headlessui/react';
import React from 'react';
import clsx from 'clsx';

export function CheckboxGroup({
  className = '',
  hasDescription = false,
  ...props
}: {
  className?: string;
  hasDescription?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="control"
      {...props}
      className={clsx(
        'flex flex-col',
        hasDescription ? 'gap-6' : 'gap-3',
        className
      )}
    />
  );
}

export function CheckboxField({
  className = '',
  ...props
}: {
  className?: string;
} & Headless.FieldProps) {
  return (
    <Headless.Field
      data-slot="field"
      {...props}
      className={clsx(
        'grid grid-cols-[1.125rem_1fr] sm:grid-cols-[1rem_1fr] items-center gap-1',
        className
      )}
    />
  );
}

interface CheckboxProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Headless.Checkbox>, 'as'> {
  className?: string;
  checked?: boolean;
  indeterminate?: boolean;
}

export function Checkbox({
  className = '',
  checked = false,
  indeterminate = false,
  disabled = false,
  ...props
}: CheckboxProps) {
  const state = checked ? 'checked' : indeterminate ? 'indeterminate' : 'unchecked';

  return (
    <Headless.Checkbox
      data-slot="control"
      checked={checked}
      disabled={disabled}
      {...props}
      className={clsx(
        // Base styles
        'relative inline-flex items-center justify-center w-5.5 h-5.5 sm:w-5 sm:h-5',
        'border border-zinc-900/15 rounded bg-white cursor-pointer',
        'transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        // Hover states
        'hover:border-zinc-900/30',
        // Checked/indeterminate states
        checked || indeterminate ? [
          'bg-white border-primary',
          'hover:border-primary'
        ] : '',
        // Disabled states
        disabled ? [
          'opacity-50 border-zinc-900/25 bg-zinc-900/5 pointer-events-none'
        ] : '',
        className
      )}
    >
      <span className="absolute inset-0 bg-white rounded-[4px] shadow-[inset_0_1px_rgba(255,255,255,0.15)]" />

      {/* Checked/indeterminate overlay */}
      {(checked || indeterminate) && (
        <span className="absolute inset-0 bg-white rounded-[4px]" />
      )}

      {/* Check mark */}
      {checked && !indeterminate && (
        <svg
          viewBox="0 0 14 14"
          fill="none"
          className={clsx(
            'absolute w-4 h-4 stroke-primary transition-opacity duration-200',
            checked ? 'opacity-100 visible' : 'opacity-0',
            disabled && 'opacity-50'
          )}
        >
          <path
            d="M3 8L6 11L11 3.5"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}

      {/* Indeterminate mark */}
      {indeterminate && (
        <svg
          viewBox="0 0 14 14"
          fill="none"
          className={clsx(
            'absolute w-4 h-4 stroke-primary transition-opacity duration-200',
            indeterminate ? 'opacity-100 visible' : 'opacity-0',
            disabled && 'opacity-50'
          )}
        >
          <path
            d="M3 7H11"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </Headless.Checkbox>
  );
}

// Composants utilitaires pour les labels et descriptions
export function CheckboxLabel({
  className = '',
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      {...props}
      className={clsx(
        'col-start-2 row-start-1 justify-self-start',
        className
      )}
    />
  );
}

export function CheckboxDescription({
  className = '',
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={clsx(
        'col-start-2 row-start-2',
        className
      )}
    />
  );
}
